import { Download, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { exportAllowances } from "@/features/reference/api";
import { ApiError } from "@/lib/api";
import { ImportAllowancesDialog } from "./standards/import-allowances-dialog";
import { IrsDefaultsTab } from "./standards/irs-defaults-tab";
import { LocalHousingTab } from "./standards/local-housing-tab";
import { NationalExpenseTab } from "./standards/national-expense-tab";
import { OtherAllowancesTab } from "./standards/other-allowances-tab";
import { VehicleTab } from "./standards/vehicle-tab";
import { ReferencePanel } from "./reference-panel";

/** Today as YYYY-MM-DD, for the effective-date default. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StandardsPage() {
  const [effectiveOn, setEffectiveOn] = useState(today());
  const [importOpen, setImportOpen] = useState(false);
  // Bumped after a JSON import so every vintage-aware tab refetches (the import
  // writes a new vintage the currently-mounted tab wouldn't otherwise see).
  const [reloadToken, setReloadToken] = useState(0);

  async function handleExport() {
    try {
      const blob = await exportAllowances(effectiveOn);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `allowances-${effectiveOn}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't export allowances.");
    }
  }

  return (
    <ReferencePanel
      title="Collection Financial Standards"
      description="Monthly allowances the 433-family hardship math subtracts from income. Versioned — editing creates a new vintage; applied vintages are never mutated."
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="effective-on" className="text-muted-foreground text-xs whitespace-nowrap">
              Effective on
            </Label>
            <Input
              id="effective-on"
              type="date"
              value={effectiveOn}
              onChange={(e) => setEffectiveOn(e.target.value)}
              className="h-8 w-[150px]"
            />
          </div>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={handleExport}>
            <Download className="size-4" /> Export
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" /> Import
          </Button>
        </div>
      }
    >
      <Tabs defaultValue="national">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="national">National expenses</TabsTrigger>
          <TabsTrigger value="housing">Local housing</TabsTrigger>
          <TabsTrigger value="vehicle">Vehicle</TabsTrigger>
          <TabsTrigger value="other">Other allowances</TabsTrigger>
          <TabsTrigger value="defaults">IRS defaults</TabsTrigger>
        </TabsList>
        <TabsContent value="national">
          <NationalExpenseTab effectiveOn={effectiveOn} reloadToken={reloadToken} />
        </TabsContent>
        <TabsContent value="housing">
          <LocalHousingTab />
        </TabsContent>
        <TabsContent value="vehicle">
          <VehicleTab effectiveOn={effectiveOn} reloadToken={reloadToken} />
        </TabsContent>
        <TabsContent value="other">
          <OtherAllowancesTab effectiveOn={effectiveOn} reloadToken={reloadToken} />
        </TabsContent>
        <TabsContent value="defaults">
          <IrsDefaultsTab />
        </TabsContent>
      </Tabs>

      <ImportAllowancesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => setReloadToken((t) => t + 1)}
      />
    </ReferencePanel>
  );
}
